import React, { useEffect, useState } from "react";
import {
  Row, Col, Card, Modal, Button, Tag, Rate, Spin, Empty, Typography, Divider
} from "antd";
import {
  HeartFilled, CalendarOutlined, TrophyOutlined,
  WhatsAppOutlined, FacebookOutlined, TwitterOutlined,
  ShareAltOutlined, ArrowRightOutlined, EnvironmentOutlined
} from "@ant-design/icons";
import { User_SuccessStoriesGet } from "../../../Api/CoreApi";
import { BASE_API } from "../../../Api/Api";
import "./SuccessStories.css";

const { Title, Text, Paragraph } = Typography;

const SuccessStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const baseurl = BASE_API;

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const response = await User_SuccessStoriesGet();
      let rawData = Array.isArray(response) ? response : response?.data || response?.results || [];

      const mapped = rawData
        .map((item, index) => {
          let imageUrl = null;
          if (item.img_url) imageUrl = item.img_url;
          else if (item.img) imageUrl = item.img.startsWith("http") ? item.img : `${baseurl}${item.img}`;

          const parsedRating = Number(item.rating);
          const validRating = !isNaN(parsedRating) && parsedRating >= 1 && parsedRating <= 5 ? parsedRating : 5;

          return {
            id: item.id || index + 1,
            title: item.name || item.title || "",
            story: item.text || item.story || "",
            image: imageUrl,
            bride_name: item.wife_name || item.bride_name || "Bride",
            groom_name: item.husband_name || item.groom_name || "Groom",
            district: item.district || "",
            state: item.state || "",
            rating: validRating,
            is_featured: item.is_featured === true || item.is_featured === "true",
            is_active: item.is_active !== false && item.is_active !== "false",
            marriage_date: item.marriage_date || null,
            created_date: item.created_at || new Date().toISOString(),
          };
        })
        .filter(s => s.is_active);

      mapped.sort((a, b) => {
        if (b.is_featured !== a.is_featured) return b.is_featured - a.is_featured;
        return new Date(b.created_date) - new Date(a.created_date);
      });

      setStories(mapped);
    } catch (error) {
      console.error("Error fetching stories:", error);
      setStories([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateAverageRating = () => {
    if (stories.length === 0) return "0.0";
    const totalRating = stories.reduce((acc, story) => acc + (story.rating || 5), 0);
    return (totalRating / stories.length).toFixed(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const handleShare = (story, platform) => {
    const text = `Check out this beautiful love story: ${story.bride_name} & ${story.groom_name} 💖`;
    const url = window.location.href;

    if (platform === 'whatsapp') window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`);
    if (platform === 'facebook') window.open(`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
    if (platform === 'twitter') window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`);
  };

  const displayedStories = showAll ? stories : stories.slice(0, 6);

  return (
    <div className="ss-section" id="success-section">
      <div className="ss-container">
        
        {/* ============ HEADER ============ */}
        <div className="ss-header">
          <Tag className="ss-badge" icon={<HeartFilled />}>Real Love Stories</Tag>
          <Title level={2} className="ss-title">
            Happy <span>Success Stories</span>
          </Title>
          <Paragraph className="ss-subtitle">
            Meet the beautiful couples who found their perfect match with us. 
            Their journey inspires thousands every day! 💕
          </Paragraph>
        </div>

        {/* ============ CONTENT ============ */}
        {loading ? (
          <div className="ss-loader">
            <Spin size="large" />
            <Text>Loading beautiful stories...</Text>
          </div>
        ) : stories.length === 0 ? (
          <Empty 
            className="ss-empty"
            image={<HeartFilled style={{ fontSize: 60, color: '#ffe4ec' }} />} 
            description={<span className="empty-text">No Stories Yet. Be the first to find love!</span>} 
          />
        ) : (
          <>
            <Row gutter={[24, 32]}>
              {displayedStories.map((story) => (
                <Col xs={24} sm={12} lg={8} key={story.id}>
                  <Card
                    hoverable
                    className="ss-story-card"
                    onClick={() => setSelectedStory(story)}
                    cover={
                      <div className="ss-card-cover">
                        {story.is_featured && (
                          <Tag color="gold" className="ss-featured-tag" icon={<TrophyOutlined />}>Featured</Tag>
                        )}
                        <img 
                          alt="couple" 
                          src={story.image || `https://ui-avatars.com/api/?name=${story.bride_name}+${story.groom_name}&background=ff0055&color=fff`} 
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Love+Story' }}
                          style={{ objectPosition: 'top center' }} 
                        />
                        <div className="ss-cover-overlay">
                          <Button shape="round" type="primary" className="read-btn">
                            Read Story <ArrowRightOutlined />
                          </Button>
                        </div>
                      </div>
                    }
                  >
                    
                    
                    <h3 className="ss-couple-name">
                      {story.bride_name} <span className="amp">&</span> {story.groom_name}
                    </h3>
                    
                    {story.title && <p className="ss-story-title">"{story.title}"</p>}
                    
                    {/* ✅ FIX: 2 lines max preview for compact card */}
                    <Paragraph ellipsis={{ rows: 2 }} className="ss-story-preview" style={{ wordBreak: 'break-word' }}>
                      {story.story || "A beautiful journey of love and togetherness..."}
                    </Paragraph>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {story.marriage_date && (
                        <div className="ss-date-badge">
                          <CalendarOutlined /> Married on {formatDate(story.marriage_date)}
                        </div>
                      )}
                      
                      {(story.district || story.state) && (
                        <div className="ss-date-badge">
                          <EnvironmentOutlined /> {[story.district, story.state].filter(Boolean).join(", ")}
                        </div>
                      )}
                    </div>

                  </Card>
                </Col>
              ))}
            </Row>

            {/* View More */}
            {stories.length > 6 && !showAll && (
              <div className="ss-view-more">
                <Button 
                  type="primary" 
                  size="large" 
                  shape="round" 
                  className="ss-view-all-btn"
                  onClick={() => setShowAll(true)}
                >
                  View All {stories.length} Stories <ArrowRightOutlined />
                </Button>
              </div>
            )}
          </>
        )}

        {/* ============ STORY MODAL ============ */}
        <Modal
          open={!!selectedStory}
          onCancel={() => setSelectedStory(null)}
          footer={null}
          width={560} // ✅ FIX: Chhota Width kar diya
          centered
          className="ss-modal"
          closeIcon={<div className="ss-modal-close">✕</div>}
          styles={{ body: { maxHeight: '78vh', overflowY: 'auto' } }} // ✅ FIX: Modal height lock & scrollable inside
        >
          {selectedStory && (
            <div className="ss-modal-content-wrapper">
              
              <div className="ss-modal-hero-wrapper">
                {selectedStory.is_featured && (
                  <Tag color="gold" className="ss-featured-tag-modal" icon={<TrophyOutlined />}>Featured Story</Tag>
                )}
                <img 
                  alt="couple" 
                  className="ss-modal-full-image"
                  src={selectedStory.image || `https://ui-avatars.com/api/?name=${selectedStory.bride_name}+${selectedStory.groom_name}&background=ff0055&color=fff`} 
                />
              </div>

              <div className="ss-modal-body">
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <Rate disabled value={selectedStory.rating} className="ss-rate-modal" allowHalf />
                  
                  <h2 className="ss-modal-names">
                    {selectedStory.bride_name} <span className="amp">&</span> {selectedStory.groom_name}
                  </h2>
                  {selectedStory.title && <h4 className="ss-modal-title">"{selectedStory.title}"</h4>}
                  
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
                    {selectedStory.marriage_date && (
                      <Tag className="ss-modal-date" icon={<CalendarOutlined />}>
                        Married on {formatDate(selectedStory.marriage_date)}
                      </Tag>
                    )}
                    
                    {(selectedStory.district || selectedStory.state) && (
                      <Tag className="ss-modal-date" icon={<EnvironmentOutlined />}>
                        {[selectedStory.district, selectedStory.state].filter(Boolean).join(", ")}
                      </Tag>
                    )}
                  </div>
                </div>

                <div className="ss-modal-story-box">
                  <h5><HeartFilled style={{ color: '#ff0055', marginRight: 8 }} /> Their Beautiful Journey</h5>
                  <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {selectedStory.story}
                  </p>
                </div>

                <Divider style={{ margin: '16px 0' }} />

                <div className="ss-modal-share">
                  <Text strong style={{ display: 'block', marginBottom: 12, color: '#64748b' }}>
                    <ShareAltOutlined /> Share this story:
                  </Text>
                  <div className="share-btn-group">
                    <Button icon={<WhatsAppOutlined />} className="sh-btn whatsapp" onClick={() => handleShare(selectedStory, 'whatsapp')}>WhatsApp</Button>
                    <Button icon={<FacebookOutlined />} className="sh-btn facebook" onClick={() => handleShare(selectedStory, 'facebook')}>Facebook</Button>
                    <Button icon={<TwitterOutlined />} className="sh-btn twitter" onClick={() => handleShare(selectedStory, 'twitter')}>Twitter</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default SuccessStories;